import type { ReactElement } from 'react';
import { useSelector } from 'react-redux';

import useOrganisationFeatures from 'app/hooks/useOrganisationFeatures';
import { selectNavigation } from 'app/store/fuse/navigationSlice';

import SidebarGroup from './components/SidebarGroup';
import SidebarItem from './components/SidebarItem';
import useSidebarItemClick from './hooks/useSidebarItemClick';
import toSidebarSections from './toSidebarSections';
import type { SidebarNavItem } from './types';

export default function SidebarNavigation(): ReactElement {
  const navigation: SidebarNavItem[] = useSelector(selectNavigation);
  const onItemClick = useSidebarItemClick();
  const { pricingLaunched, isFeatureHidden } = useOrganisationFeatures();

  // A Plus page support switched off for the organisation is left out, not tagged Plus+.
  const isHidden = (entry: SidebarNavItem) =>
    pricingLaunched && !!entry.plusFeature && isFeatureHidden(entry.plusFeature);
  const visible = navigation
    .filter((entry) => !isHidden(entry))
    .map((entry) =>
      entry.children
        ? { ...entry, children: entry.children.filter((item) => !isHidden(item)) }
        : entry
    );

  return (
    <nav aria-label="Main" className="vtwo:flex vtwo:flex-col vtwo:gap-4">
      {toSidebarSections(visible).map((section) => {
        if (section.kind === 'group') {
          return (
            <SidebarGroup key={section.group.id} group={section.group} onItemClick={onItemClick} />
          );
        }

        return (
          <div key={section.items[0].id} className="vtwo:flex vtwo:flex-col vtwo:gap-0.5">
            {section.items.map((item) => (
              <SidebarItem key={item.id} item={item} onItemClick={onItemClick} />
            ))}
          </div>
        );
      })}
    </nav>
  );
}
